/**
 * Bold, italic and underline in a line that is a plain string (DOP-660): a
 * section's heading, a sub-heading, a company fact, the tagline.
 *
 * The formatting is written into the string itself, as a small mark-up:
 * `**bold**`, `*italic*`, `__underline__`. So the string saves, versions and
 * exports as it always did, and every place that prints it either draws the
 * marks (`renderInline`) or takes them off (`stripMarks`).
 *
 * A mark opens before a word and closes after one: a star with a space on
 * the wrong side ("5 * 3") is a star. A mark left open is its own
 * characters. `\*` and `\_` are the characters themselves.
 *
 * The backend reads the same mark-up (`services/inline_marks.py`): the two
 * must agree.
 */

import React from "react";

export type InlineMark = "bold" | "italic" | "underline";

const BOLD = 1;
const ITALIC = 2;
const UNDERLINE = 4;
const BIT: Record<InlineMark, number> = {
  bold: BOLD,
  italic: ITALIC,
  underline: UNDERLINE,
};
const BITS = [BOLD, ITALIC, UNDERLINE];

const SPACE = /\s/;

interface Parsed {
  /** The characters as read, the mark-up taken off. */
  chars: string[];
  /** Each character's marks, as bits. */
  marks: number[];
  /** For each place in the string as written (0..length), the character it is before. */
  at: number[];
}

interface Opener {
  bit: number;
  index: number;
  literal: string;
  rawEnd: number;
}

/** The marks a run of `n` stars or underscores turns on or off; 0 when it is only characters. */
const runBits = (char: string, n: number): number => {
  if (char === "_") return n === 2 ? UNDERLINE : 0;
  if (n === 1) return ITALIC;
  if (n === 2) return BOLD;
  return n === 3 ? BOLD | ITALIC : 0;
};

function parse(value: string): Parsed {
  const chars: string[] = [];
  const marks: number[] = [];
  const at: number[] = new Array(value.length + 1);
  const opened: Opener[] = [];
  let open = 0;
  let i = 0;
  while (i < value.length) {
    at[i] = chars.length;
    const c = value[i];
    if (c === "\\" && (value[i + 1] === "*" || value[i + 1] === "_")) {
      at[i + 1] = chars.length;
      chars.push(value[i + 1]);
      marks.push(open);
      i += 2;
      continue;
    }
    if (c !== "*" && c !== "_") {
      chars.push(c);
      marks.push(open);
      i += 1;
      continue;
    }
    let j = i;
    while (value[j] === c) j += 1;
    for (let k = i; k < j; k += 1) at[k] = chars.length;
    const bits = runBits(c, j - i);
    const canClose = i > 0 && !SPACE.test(value[i - 1]);
    const canOpen = j < value.length && !SPACE.test(value[j]);
    const turns = BITS.filter((bit) => bits & bit);
    let reads = turns.length > 0;
    for (const bit of turns)
      if (!(open & bit ? canClose : canOpen)) reads = false;
    if (!reads) {
      for (let k = i; k < j; k += 1) {
        chars.push(c);
        marks.push(open);
      }
    } else {
      for (const bit of turns) {
        if (open & bit) {
          open &= ~bit;
          for (let o = opened.length - 1; o >= 0; o -= 1)
            if (opened[o].bit === bit) opened.splice(o, 1);
        } else {
          open |= bit;
          const literal = bit === UNDERLINE ? "__" : bit === BOLD ? "**" : "*";
          opened.push({ bit, index: chars.length, literal, rawEnd: j });
        }
      }
    }
    i = j;
  }
  at[value.length] = chars.length;
  // A mark never closed is its own characters, and marks nothing.
  for (let o = opened.length - 1; o >= 0; o -= 1) {
    const { bit, index, literal, rawEnd } = opened[o];
    for (let k = index; k < marks.length; k += 1) marks[k] &= ~bit;
    chars.splice(index, 0, ...literal.split(""));
    marks.splice(index, 0, ...literal.split("").map(() => 0));
    for (let r = rawEnd; r <= value.length; r += 1) at[r] += literal.length;
  }
  return { chars, marks, at };
}

/** Whether the string could hold a mark at all: most never do. */
const mayHold = (value: string): boolean =>
  value.includes("*") || value.includes("_");

/** The string as plain words, for a place that takes no formatting: a list, a search, a file's name. */
export function stripMarks(value: string): string {
  return mayHold(value) ? parse(value).chars.join("") : value;
}

/** A mark sits on words: the spaces at its ends are left out of it. */
function trimmed(chars: string[], marks: number[]): number[] {
  const next = [...marks];
  BITS.forEach((bit) => {
    let i = 0;
    while (i < next.length) {
      if (!(next[i] & bit)) {
        i += 1;
        continue;
      }
      let j = i;
      while (j < next.length && next[j] & bit) j += 1;
      let a = i;
      let b = j;
      while (a < b && SPACE.test(chars[a])) a += 1;
      while (b > a && SPACE.test(chars[b - 1])) b -= 1;
      for (let k = i; k < a; k += 1) next[k] &= ~bit;
      for (let k = b; k < j; k += 1) next[k] &= ~bit;
      i = j;
    }
  });
  return next;
}

interface Written {
  value: string;
  /** Where each character starts and ends in the string as written. */
  from: number[];
  to: number[];
}

const stars = (bits: number): string =>
  "*".repeat((bits & BOLD ? 2 : 0) + (bits & ITALIC ? 1 : 0));

function write(chars: string[], marks: number[], escape: boolean): Written {
  let value = "";
  const from: number[] = [];
  const to: number[] = [];
  let was = 0;
  for (let i = 0; i <= chars.length; i += 1) {
    const now = i < chars.length ? marks[i] : 0;
    const closing = was & ~now;
    const opening = now & ~was;
    value += stars(closing) + (closing & UNDERLINE ? "__" : "");
    value += (opening & UNDERLINE ? "__" : "") + stars(opening);
    was = now;
    if (i === chars.length) break;
    from.push(value.length);
    value +=
      escape && (chars[i] === "*" || chars[i] === "_")
        ? `\\${chars[i]}`
        : chars[i];
    to.push(value.length);
  }
  return { value, from, to };
}

/** The characters and their marks as a string that reads back the same, or null when none does. */
function serialise(chars: string[], marks: number[]): Written | null {
  for (const escape of [false, true]) {
    const written = write(chars, marks, escape);
    const back = parse(written.value);
    if (
      back.chars.join("") === chars.join("") &&
      back.marks.every((m, i) => m === marks[i])
    )
      return written;
  }
  return null;
}

/** The characters a selection covers: the word the caret is in when nothing is selected. */
function range(parsed: Parsed, start: number, end: number): [number, number] {
  const { chars, at } = parsed;
  let a = at[Math.max(0, Math.min(start, at.length - 1))];
  let b = at[Math.max(0, Math.min(end, at.length - 1))];
  if (a === b) {
    while (a > 0 && !SPACE.test(chars[a - 1])) a -= 1;
    while (b < chars.length && !SPACE.test(chars[b])) b += 1;
  }
  while (a < b && SPACE.test(chars[a])) a += 1;
  while (b > a && SPACE.test(chars[b - 1])) b -= 1;
  return [a, b];
}

export interface Toggled {
  value: string;
  /** The selection, over the same words, in the new string. */
  start: number;
  end: number;
}

/**
 * The string with a mark put on the selected words, or taken off when all
 * of them already have it. With nothing selected, the word the caret is in.
 * Null when there is nothing to mark.
 */
export function toggleMark(
  value: string,
  start: number,
  end: number,
  mark: InlineMark,
): Toggled | null {
  const parsed = parse(value);
  const [a, b] = range(parsed, start, end);
  if (a >= b) return null;
  const bit = BIT[mark];
  const marks = [...parsed.marks];
  const all = marks.slice(a, b).every((m) => m & bit);
  for (let i = a; i < b; i += 1)
    marks[i] = all ? marks[i] & ~bit : marks[i] | bit;
  const written = serialise(parsed.chars, trimmed(parsed.chars, marks));
  if (!written) return null;
  if (start === end) {
    // The caret stays in its word, where it was.
    const caret = parsed.at[Math.max(0, Math.min(start, parsed.at.length - 1))];
    const place = caret > a ? written.to[caret - 1] : written.from[caret];
    return { value: written.value, start: place, end: place };
  }
  return {
    value: written.value,
    start: written.from[a],
    end: written.to[b - 1],
  };
}

/** The marks every selected word has: the ones the toolbar shows lit. */
export function marksAt(
  value: string,
  start: number,
  end: number,
): Set<InlineMark> {
  const lit = new Set<InlineMark>();
  if (!mayHold(value)) return lit;
  const parsed = parse(value);
  const [a, b] = range(parsed, start, end);
  if (a >= b) return lit;
  (Object.keys(BIT) as InlineMark[]).forEach((mark) => {
    if (parsed.marks.slice(a, b).every((m) => m & BIT[mark])) lit.add(mark);
  });
  return lit;
}

/**
 * The string as the memo prints it, its marks drawn. `transform` changes
 * the words' case (a sub-heading read in sentence case) and must keep their
 * length.
 */
/** The plain characters `from`..`to` of a parsed value, drawn with their marks. */
function renderParsed(
  text: string,
  marks: number[],
  from: number,
  to: number,
): React.ReactNode {
  const nodes: React.ReactNode[] = [];
  let i = from;
  while (i < to) {
    let j = i;
    while (j < to && marks[j] === marks[i]) j += 1;
    let node: React.ReactNode = text.slice(i, j);
    if (marks[i] & UNDERLINE) node = <u>{node}</u>;
    if (marks[i] & ITALIC) node = <em>{node}</em>;
    if (marks[i] & BOLD) node = <strong>{node}</strong>;
    // Runs are positional within an immutable string.
    nodes.push(<React.Fragment key={i}>{node}</React.Fragment>);
    i = j;
  }
  return <>{nodes}</>;
}

export function renderInline(
  value: string,
  transform?: (plain: string) => string,
): React.ReactNode {
  if (!mayHold(value)) return transform ? transform(value) : value;
  const { chars, marks } = parse(value);
  const plain = chars.join("");
  const shown = transform ? transform(plain) : plain;
  const text = shown.length === plain.length ? shown : plain;
  if (marks.every((m) => m === 0)) return text;
  return renderParsed(text, marks, 0, chars.length);
}

/**
 * A slice of the value by PLAIN positions (`stripMarks(value)` indices),
 * drawn with its marks. A person's edit of a company fact is shown as the
 * document's reading with only the added words marked (DOP-658), and that
 * split must not cut a bold span's delimiters.
 */
export function renderInlineSlice(
  value: string,
  from: number,
  to: number,
): React.ReactNode {
  if (!mayHold(value)) return value.slice(from, to);
  const { chars, marks } = parse(value);
  return renderParsed(
    chars.join(""),
    marks,
    Math.max(0, from),
    Math.min(chars.length, to),
  );
}

// ------------------------------------------------------------------ in a field

/** A field that takes formatting says so (`MemoEditField`'s `marks`). */
export const MARKS_ATTR = "data-inline-marks";

/** The field being typed in, when the caret is in one. */
export const focusedField = ():
  HTMLTextAreaElement | HTMLInputElement | null => {
  const active = document.activeElement;
  return active instanceof HTMLTextAreaElement ||
    active instanceof HTMLInputElement
    ? active
    : null;
};

export const takesMarks = (field: Element | null): boolean =>
  field?.hasAttribute(MARKS_ATTR) ?? false;

/**
 * Puts a mark on the field's selected words, where they are: the caret
 * never leaves the field and the selection stays on the same words. The
 * field hears it as typing, so its draft follows.
 */
export function toggleFieldMark(
  field: HTMLTextAreaElement | HTMLInputElement,
  mark: InlineMark,
): boolean {
  const toggled = toggleMark(
    field.value,
    field.selectionStart ?? 0,
    field.selectionEnd ?? 0,
    mark,
  );
  if (!toggled) return false;
  const proto =
    field instanceof HTMLTextAreaElement
      ? HTMLTextAreaElement.prototype
      : HTMLInputElement.prototype;
  // React keeps its own copy of the value: only the element's own setter reaches it.
  Object.getOwnPropertyDescriptor(proto, "value")?.set?.call(
    field,
    toggled.value,
  );
  field.dispatchEvent(new Event("input", { bubbles: true }));
  field.setSelectionRange(toggled.start, toggled.end);
  return true;
}

/** Ctrl/⌘ + B, I or U, as the mark it asks for. */
/** Whether this is a Mac: its keys say ⌘ where the others say Ctrl. */
export const isMacPlatform = (): boolean =>
  typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent || "");

/** The shortcut's label for this computer: "⌘K" on a Mac, "Ctrl+K" elsewhere. */
export const shortcutLabel = (key: string, shift = false): string =>
  isMacPlatform() ? `⌘${shift ? "⇧" : ""}${key}` : `Ctrl+${shift ? "Shift+" : ""}${key}`;

export function shortcutMark(event: {
  key: string;
  metaKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
  shiftKey: boolean;
}): InlineMark | null {
  if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey)
    return null;
  const key = event.key.toLowerCase();
  return key === "b"
    ? "bold"
    : key === "i"
      ? "italic"
      : key === "u"
        ? "underline"
        : null;
}
